import { useLoadMapConfig } from '@/hooks/load/useLoadMapConfig'
import { useClosePopupOnMapClick } from '@/hooks/useClosePopupOnMapClick'
import { useMobile } from '@/hooks/useMobile'
import { useMaybeModule } from '@/hooks/useModule'
import { mapModule } from '@/routes'
import { getMapConfig, getModules } from '@/stores/config'
import { useSelector, useStore } from '@/stores/hooks'
import { isMobile } from '@/stores/meta'
import { getBoardHeight, getPopupHovered, getPopupOpened } from '@/stores/temp'
import type { CustomLocationHook } from '@/types/useCustomLocation'
import type { ModuleObject } from '@/util/modules'
import type { MaybeDesktopMobile } from '@common/types/config/MapConfig'
import {
	MapRenderer,
	useMap,
	useMapBounds,
	useMapReady,
	useZoom,
} from '@lc/libmap-react'
import type { Padding } from '@lc/libmap-types'
import { useBuildOnce, useFollow, useOnChangeEffect } from '@lc/libreact'
import { idAssert } from '@lc/libts'
import { eq } from '@lc/libutils'
import { useEffect, useState } from 'react'
import { Switch, useLocation, useSearch } from 'wouter'
import styles from './Map.scss'
import { MapOptions } from './map/map-options/MapOptions'
import { MapPopup } from './map/render/Popup/MapPopup'
import {
	popupContent,
	type PopupLifecycleParams,
} from './map/render/Popup/popupContent'

function computePadding(
	modules: ModuleObject[],
	mobile: boolean,
	boardHeight?: number,
) {
	const hasModeSelector = new Set(modules.map(x => x.mode)).size > 1
	if (mobile)
		return {
			left: 20,
			top: hasModeSelector ? 100 : 20,
			right: 20,
			bottom: boardHeight ? boardHeight + 20 : 570,
		}
	else
		return {
			left: 550,
			top: hasModeSelector ? 150 : 50,
			right: 70,
			bottom: 70,
		}
}

function applyDesktopMobile<T>(
	prop: MaybeDesktopMobile<T>,
	mobile: boolean,
): T {
	if (
		typeof prop === 'object' &&
		prop &&
		'desktop' in prop &&
		'mobile' in prop
	) {
		idAssert<{ desktop: T; mobile: T }>(prop)
		return prop[mobile ? 'mobile' : 'desktop']
	} else {
		return prop
	}
}

function MapConfigDebug() {
	const mapConfig = useSelector(getMapConfig)
	if (!mapConfig) return <p>Map config loading...</p>

	const { minZoom, maxZoom, maxBounds } = mapConfig

	return (
		<>
			<p>
				Min zoom: {typeof minZoom === 'number' ? minZoom : <>(unset)</>}
			</p>
			<p>
				Max zoom: {typeof maxZoom === 'number' ? maxZoom : <>(unset)</>}
			</p>
			<p>
				Max bounds:{' '}
				{maxBounds ? JSON.stringify(maxBounds) : <>(unset)</>}
			</p>
		</>
	)
}

function MapDebug() {
	const ready = useMapReady()
	const zoom = useZoom()
	const bounds = useMapBounds()

	return (
		<div
			style={{
				position: 'fixed',
				bottom: '2rem',
				right: '2rem',
				zIndex: 10,
				background: 'white',
				borderRadius: '1rem',
				padding: '1rem',
			}}
		>
			<p>Ready: {JSON.stringify(ready)}</p>
			<p>Zoom: {zoom}</p>
			<p>Bounds: {JSON.stringify(bounds)}</p>
			<MapConfigDebug />
		</div>
	)
}

function Map() {
	const modules = useSelector(getModules)
	const mobile = useMobile()

	useLoadMapConfig({ level: 1 })
	const mapConfig = useSelector(getMapConfig)
	const map = useMap()
	const [hidden, setHidden] = useState(false)
	const mapReady = useMapReady()
	const store = useStore()

	const padding = useBuildOnce(() => {
		const state = store.getState()
		const mobile = isMobile(state)
		const boardHeight = getBoardHeight(state)
		return computePadding(modules, mobile, boardHeight)
	})
	const popupHovered = useSelector(getPopupHovered)
	const popupOpened = useSelector(getPopupOpened)

	useEffect(() => {
		if (!map) return
		if (!mapReady) return
		let prevPadding: Padding | null = null
		const update = () => {
			const state = store.getState()
			const mobile = isMobile(state)
			const modules = getModules(state)
			const boardHeight = getBoardHeight(state)
			const padding = computePadding(modules, mobile, boardHeight)
			if (eq(prevPadding, padding)) return
			prevPadding = padding
			map.setPadding(padding)
		}
		update()
		return store.subscribe(update)
	}, [store, map, mapReady])

	const [location, setLocation] = useLocation<CustomLocationHook>()
	const search = useSearch()
	const module = useMaybeModule()
	const popupLifecycleParams = useFollow<PopupLifecycleParams>({
		module,
		store,
		search,
		location,
		setLocation,
	})

	useOnChangeEffect(() => {
		if (!mapReady) return
		if (!popupOpened) return

		const popupContentForType = popupContent[popupOpened.type]
		if (!popupContentForType)
			throw new Error(`No such popup type ${popupOpened.type}`)

		popupContentForType.onMount?.(popupOpened, popupLifecycleParams.current)

		return () => {
			popupContentForType.onUnmount?.(
				popupOpened,
				popupLifecycleParams.current,
			)
		}
	}, [mapReady, popupOpened])

	useClosePopupOnMapClick()

	if (!mapConfig) return null

	const {
		zoom,
		center,
		maxBounds,
		minZoom,
		maxZoom,
		styles: [{ options }],
	} = mapConfig

	return (
		<>
			<MapRenderer
				aria-hidden="true"
				className={styles.container}
				zoom={applyDesktopMobile(zoom, mobile)}
				center={applyDesktopMobile(center, mobile)}
				minZoom={minZoom}
				maxZoom={maxZoom}
				maxBounds={maxBounds}
				padding={padding}
				options={options}
			/>
			{!!+process.env.DEBUG! && <MapDebug />}
			{mapReady && <MapOptions hidden={hidden} setHidden={setHidden} />}
			{!hidden && <Switch>{modules.map(mapModule)}</Switch>}
			{popupOpened && (
				<MapPopup
					permanent
					key={`${popupOpened.type}/${popupOpened.id}/permanent`}
					{...popupOpened}
				/>
			)}
			{popupHovered &&
				(!popupOpened ||
					popupOpened.id !== popupHovered.id ||
					popupOpened.type !== popupHovered.type) && (
					<MapPopup
						key={`${popupHovered.type}/${popupHovered.id}`}
						{...popupHovered}
					/>
				)}
		</>
	)
}

export default Map
